import React, { useEffect, useState } from "react";

import { useQuery } from "@apollo/client";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  InputAdornment,
  RadioGroup,
  TextField,
} from "@mui/material";
import { makeStyles, withStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import { SearchAppointmentListItemFragment } from "@/gql/graphql";
import useDebounce from "@/hooks/useDebounce";
import { theme } from "@/theme/customTheme";
import { colors } from "@/theme/overrides/colors";

import SearchAppointmentListItem from "./SearchAppointmentListItem";
import SelectedAppointment from "./SelectedAppointmentView";

const useStyles = makeStyles()({
  container: {
    minWidth: 350,
    position: "relative",
    [theme.breakpoints.down("md")]: {
      minWidth: 0,
      width: "100%",
    },
  },
  listContainer: {
    position: "absolute",
    width: "100%",
    marginTop: 1,
    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
    backgroundColor: colors.white,
    zIndex: 9,
  },
  loading: {
    padding: 20,
  },
  loadingText: {
    fontSize: 14,
    textAlign: "center",
  },
  list: {
    display: "flex",
    flexDirection: "column",
    maxHeight: 350,
    overflow: "hidden",
    overflowY: "auto",
  },
  optionContainer: {
    padding: 20,
    display: "flex",
    justifyContent: "flex-end",
    borderTopStyle: "solid",
    borderTopWidth: 1,
    borderTopColor: "#C8CED8",
  },
});

const StyledTextField = withStyles(TextField, {
  root: {
    "& .MuiInputBase-input": {
      display: "flex",
      alignItems: "center",
      height: 52,
      padding: 0,
      paddingLeft: 16,
      paddingRight: 28,
    },
  },
});

const StyledButton = withStyles(Button, (theme) => ({
  contained: {
    height: theme.typography.pxToRem(32),
  },
}));

type SeachAppointmentListViewProps = {
  useRadio?: boolean;
  onSelectedID: (id: string) => void;
  onSelectedAppointmentID?: (id: string | null) => void;
  onClearSelected?: () => void;
  errorMessage?: string | undefined;
};

const searchAppointmentListViewQuery = graphql(`
  query SeachAppointmentListView($searchText: String) {
    searchAppointments(searchText: $searchText) {
      ...SearchAppointmentListItem
    }
  }
`);

const SeachAppointmentListView: React.FC<SeachAppointmentListViewProps> = ({
  onSelectedID,
  onClearSelected,
  onSelectedAppointmentID,
  useRadio = false,
  errorMessage,
}) => {
  const { classes } = useStyles();
  const [showList, setShowList] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [hasSelected, setHasSelected] = useState(false);
  const [selected, setSelected] =
    useState<SearchAppointmentListItemFragment | null>(null);
  const selectorRef = React.useRef<HTMLInputElement>(null);
  const closeList = () => setShowList(false);
  const openList = () => setShowList(true);
  const { loading, data } = useQuery(searchAppointmentListViewQuery, {
    variables: {
      searchText: searchValue,
    },
  });
  const searchAppointments = data?.searchAppointments;
  const setDebouncedSearchValue = useDebounce(setSearchValue, 500);

  function handleSearch(event: React.ChangeEvent<HTMLInputElement>) {
    const value = event.target.value.toLowerCase();
    if (value == "") {
      closeList();
    } else {
      openList();
    }
    setDebouncedSearchValue(value);
  }

  function handleSelectedUser(
    clientId: string | null | undefined,
    searchAppointmentDocument: SearchAppointmentListItemFragment
  ) {
    setSelected(searchAppointmentDocument);
    const { searchableType, searchableId } = searchAppointmentDocument;
    if (searchableType == "Appointment") {
      onSelectedAppointmentID && onSelectedAppointmentID(searchableId);
    } else {
      onSelectedAppointmentID && onSelectedAppointmentID(null);
    }
    if (!useRadio) {
      onSelectedID(clientId ?? "");
      setHasSelected(true);
      closeList();
    }
  }

  function clearSelected() {
    onClearSelected && onClearSelected();
    onSelectedAppointmentID && onSelectedAppointmentID(null);
    setSearchValue("");
    setSelected(null);
    setHasSelected(false);
  }

  useEffect(() => {
    function clickHandler({ target }: MouseEvent) {
      if (
        selectorRef.current &&
        !selectorRef.current.contains(target as Node)
      ) {
        closeList();
      }
    }
    document.addEventListener("mousedown", clickHandler);
    return () => {
      document.removeEventListener("mousedown", clickHandler);
    };
  }, [selectorRef]);

  return (
    <div ref={selectorRef} className={classes.container}>
      {hasSelected ? (
        <SelectedAppointment
          searchDocument={selected}
          onClose={clearSelected}
        />
      ) : (
        <StyledTextField
          error={!!errorMessage}
          onChange={handleSearch}
          fullWidth
          placeholder="Search"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon htmlColor={colors.primary} />
              </InputAdornment>
            ),
          }}
        />
      )}
      {showList && (
        <Box className={classes.listContainer}>
          {!loading && (
            <Box>
              <Box className={classes.list}>
                <RadioGroup name="user">
                  {searchAppointments?.map((data, index) => (
                    <SearchAppointmentListItem
                      key={index}
                      searchDocument={data}
                      useRadio={useRadio}
                      onSelect={() => handleSelectedUser(data.clientId, data)}
                    />
                  ))}
                </RadioGroup>
              </Box>
              {useRadio && (
                <Box className={classes.optionContainer}>
                  <StyledButton
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      if (selected) {
                        onSelectedID(selected.clientId);
                        setHasSelected(true);
                      }
                      closeList();
                    }}
                  >
                    Done
                  </StyledButton>
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}
    </div>
  );
};

export default SeachAppointmentListView;
